import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/salary.css";

const API_BASE_URL = "https://growrise.shop";

const Salary = () => {
  const navigate = useNavigate();

  const [salary, setSalary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [message, setMessage] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const fetchSalaryData = async () => {
    try {
      setLoading(true);
      setMessage("");

      const token = getToken();

      if (!token) {
        navigate("/login", { replace: true });
        return;
      }

      const statusResponse = await fetch(`${API_BASE_URL}/api/salary/status`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const statusData = await statusResponse.json();

      if (!statusResponse.ok) {
        setMessage(statusData.message || "Failed to fetch salary status");
        return;
      }

      setSalary(statusData.salary || null);

      const historyResponse = await fetch(`${API_BASE_URL}/api/salary/history`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const historyData = await historyResponse.json();

      if (historyResponse.ok) {
        setHistory(historyData.claims || []);
      }
    } catch (error) {
      console.error("Salary fetch error:", error);
      setMessage("Server error while fetching salary data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaryData();
  }, []);

  const handleClaimSalary = async () => {
    try {
      setClaiming(true);
      setMessage("");

      const token = getToken();

      if (!token) {
        navigate("/login", { replace: true });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/salary/claim`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.message || "Failed to claim salary");
        if (data.salary) {
          setSalary(data.salary);
        }
        return;
      }

      setMessage(data.message || "Salary claimed successfully");
      await fetchSalaryData();
    } catch (error) {
      console.error("Salary claim error:", error);
      setMessage("Server error while claiming salary");
    } finally {
      setClaiming(false);
    }
  };

  const formatAmount = (amount) => {
    return `$${Number(amount || 0).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="salary-page">
        <div className="salary-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="salary-page">
      <div className="salary-header">
        <button
          type="button"
          className="salary-back-btn"
          onClick={() => navigate("/dashboard")}
        >
          Back
        </button>

        <div>
          <h2>Weekly Salary</h2>
          <p>Complete your active direct team and claim weekly salary.</p>
        </div>
      </div>

      {message && <div className="salary-message">{message}</div>}

      <div className="salary-card">
        <div className="salary-card-top">
          <span>Available Salary</span>
          <h1>{formatAmount(salary?.salaryAmount)}</h1>
        </div>

        <div className="salary-stats-grid">
          <div className="salary-stat-box">
            <span>Total Direct Members</span>
            <strong>{salary?.totalDirectMembers || 0}</strong>
          </div>

          <div className="salary-stat-box">
            <span>Active Direct Members</span>
            <strong>{salary?.activeDirectMembers || 0}</strong>
          </div>

          <div className="salary-stat-box">
            <span>First Target</span>
            <strong>{salary?.requiredForFirstSalary || 5}</strong>
          </div>

          <div className="salary-stat-box">
            <span>Second Target</span>
            <strong>{salary?.requiredForSecondSalary || 10}</strong>
          </div>
        </div>

        <div className="salary-rule-box">
          <div>
            <span>5 active direct members</span>
            <strong>$15 weekly</strong>
          </div>

          <div>
            <span>10 active direct members</span>
            <strong>$30 weekly</strong>
          </div>
        </div>

        <button
          type="button"
          className="salary-claim-btn"
          onClick={handleClaimSalary}
          disabled={!salary?.canClaim || claiming}
        >
          {claiming
            ? "Claiming..."
            : salary?.alreadyClaimed
            ? "Already Claimed This Week"
            : salary?.canClaim
            ? "Claim Salary"
            : "Not Qualified Yet"}
        </button>
      </div>

      <div className="salary-section">
        <h3>Direct Team</h3>

        {salary?.directMembers?.length > 0 ? (
          <div className="salary-team-list">
            {salary.directMembers.map((member) => (
              <div className="salary-team-item" key={member.id}>
                <div>
                  <strong>{member.name}</strong>
                  <span>{member.email}</span>
                </div>

                <span
                  className={
                    member.isActive
                      ? "salary-status-active"
                      : "salary-status-inactive"
                  }
                >
                  {member.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="salary-empty">No direct team members found.</div>
        )}
      </div>

      <div className="salary-section">
        <h3>Salary History</h3>

        {history.length > 0 ? (
          <div className="salary-history-list">
            {history.map((item) => (
              <div className="salary-history-item" key={item.id}>
                <div>
                  <strong>{formatAmount(item.amount)}</strong>
                  <span>{formatDate(item.createdAt)}</span>
                </div>

                <span className="salary-history-status">
                  {item.status || "claimed"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="salary-empty">No salary history found.</div>
        )}
      </div>
    </div>
  );
};

export default Salary;